import { useState } from 'react';
import postResolvePendingCustomer, {
  ResolveDecision,
} from '../services/postResolvePendingCustomer';

interface AlertMessage {
  description: string;
  variant: 'error' | 'success';
}

interface Status {
  loading: boolean;
  alertMessage: AlertMessage;
  success: boolean;
  error: boolean;
}

export type PendingCustomerActions = 'APPROVE' | 'REJECT';

export interface SelectedPendingCustomer {
  id: string;
  customerName: string;
  cuit: string;
  action: PendingCustomerActions;
}

type MessageType = {
  [key: string]: AlertMessage;
};

const ACTION_TO_DECISION: Record<PendingCustomerActions, ResolveDecision> = {
  APPROVE: 'APPROVED',
  REJECT: 'REJECTED',
};

/**
 * Approve / reject pending customer registration state hook
 * @param handleRefresh Function to refresh the list
 */
const useResolvePendingCustomer = (handleRefresh: () => void) => {
  const MESSAGES: MessageType = {
    ERROR_GENERIC: {
      description: 'COMMON:ERRORS:IT_ERROR',
      variant: 'error',
    },
    SUCCESS_APPROVED: {
      description: 'PENDING_CUSTOMERS:ALERT_MESSAGES:APPROVED',
      variant: 'success',
    },
    SUCCESS_REJECTED: {
      description: 'PENDING_CUSTOMERS:ALERT_MESSAGES:REJECTED',
      variant: 'success',
    },
    ERROR_APPROVED: {
      description: 'PENDING_CUSTOMERS:ALERT_MESSAGES:APPROVED_ERROR',
      variant: 'error',
    },
    ERROR_REJECTED: {
      description: 'PENDING_CUSTOMERS:ALERT_MESSAGES:REJECTED_ERROR',
      variant: 'error',
    },
  } as const;

  const [status, setStatus] = useState<Status>({
    loading: false,
    success: false,
    error: false,
    alertMessage: MESSAGES.ERROR_GENERIC,
  });

  const [selectedCustomer, setSelectedCustomer] = useState<SelectedPendingCustomer>({
    id: '',
    customerName: '',
    cuit: '',
    action: 'APPROVE',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Function to set the error state.
   * @param {boolean} error - The error state.
   */
  const setIsError = (error: boolean) => {
    setStatus((prevState) => ({ ...prevState, error }));
  };

  /**
   * Function to set the success state.
   * @param {boolean} success - The success state.
   */
  const setIsSuccess = (success: boolean) => {
    setStatus((prevState) => ({ ...prevState, success }));
  };

  /**
   * Function to set the success/error state with message.
   * @param success - True if success.
   * @param message - The message.
   */
  const setResponseMessage = (success: boolean, message: AlertMessage) => {
    setStatus({
      loading: false,
      error: !success,
      success,
      alertMessage: message,
    });
  };

  /**
   * Resolves a pending customer with APPROVED or REJECTED decision
   * @async
   * @param body - body payload request
   * @param body.id - pending customer id
   * @param body.decision - APPROVED or REJECTED
   */
  async function resolveCustomer(body: { id: string; decision: ResolveDecision }) {
    try {
      setStatus((prevState) => ({ ...prevState, loading: true }));
      await postResolvePendingCustomer(body);
      setTimeout(() => {
        handleRefresh();
        setResponseMessage(
          true,
          body.decision === 'APPROVED' ? MESSAGES.SUCCESS_APPROVED : MESSAGES.SUCCESS_REJECTED
        );
      }, 1000);
    } catch (error) {
      setResponseMessage(
        false,
        body.decision === 'APPROVED' ? MESSAGES.ERROR_APPROVED : MESSAGES.ERROR_REJECTED
      );
    } finally {
      setIsModalOpen(false);
    }
  }

  /**
   * Handle submit
   */
  const handleSubmit = () => {
    resolveCustomer({
      id: selectedCustomer.id,
      decision: ACTION_TO_DECISION[selectedCustomer.action],
    });
  };

  /**
   * On click action buttons
   * @param customer Selected pending customer
   */
  const onActionClick = (customer: SelectedPendingCustomer) => {
    setIsModalOpen(true);
    setSelectedCustomer(customer);
  };

  const { loading: isLoading, success: isSuccess, error: isError, alertMessage } = status;

  return {
    isLoading,
    isSuccess,
    isError,
    alertMessage,
    setIsSuccess,
    setIsError,
    handleSubmit,
    isModalOpen,
    setIsModalOpen,
    selectedCustomer,
    setSelectedCustomer,
    onActionClick,
  };
};

export default useResolvePendingCustomer;
